import { define, reactive, toJS } from '@rehaje/reactive'
import {
  assign,
  createMiddleware,
  entries,
  fromEntries,
  hasOwn,
  isArray,
  isInheritProto,
  keys,
  values
} from '@rehaje/shared'
import { compareVersions } from 'compare-versions'

import { FatalAssertInheritError, LoggerMigrationError } from '../errors'
import { TypedDefinition, TypedNode, TypedVariable, ViewModel } from '../models'
import { InternalPlugins } from '../plugins'

import type {
  Middleware,
  MiddlewareStartup,
  MigrationTypes,
  Next,
  PluginEnvironment,
  PluginLike,
  PluginMigrations,
  PluginVisitors,
  RehajePlugin,
  RequiredPluginVisitors,
  StorePluginPrototype,
  TypedDefinitionConstructor,
  TypedNodeConstructor,
  TypedSpecific,
  ViewComponent,
  ViewModelConstructor,
  VisitorMigrations
} from '../types'
import type { Store } from './Store'
import type { TClass, TRecord, ValueOf } from '@rehaje/shared'

/**
 * 插件引擎
 */
export class PluginStore {
  static __r_migration_cache = new Map<string, ValueOf<VisitorMigrations>>()

  __r_migration_cache = new Map<
    string,
    (specific: TypedSpecific) => TypedSpecific
  >()

  /**
   * 组件集合
   */
  @reactive()
  declare components: Record<string, ViewComponent>

  /**
   * ADT 类型定义集合
   */
  @reactive()
  declare definitions: Record<string, TypedDefinitionConstructor>

  /**
   * 环境变量集合
   */
  @reactive()
  declare environments: TypedVariable[]

  /**
   * 中间件集合
   */
  @reactive()
  declare middlewares: Record<string, MiddlewareStartup>

  /**
   * 升级脚本集合
   */
  @reactive()
  declare migrations: PluginMigrations

  /**
   * ViewModel 类集合
   */
  @reactive()
  declare models: Record<string, ViewModelConstructor>

  /**
   * TypedNode 类集合
   */
  @reactive()
  declare nodes: Record<string, TypedNodeConstructor>

  /**
   * 插件集合
   */
  @reactive('shallow')
  plugins: PluginLike[]

  /**
   * 类型访问器集合
   */
  @reactive()
  declare visitors: Required<PluginVisitors>

  constructor(public store: Store) {
    this.plugins = InternalPlugins.concat(store.options.plugins ?? [])
    this.__r_setupPlugins()
    define(this)
  }

  __r_applyMiddleware<T>(self: any, type: string, payload: T, next: Next<T>): T {
    const middleware = this.middlewares[type]
    if (middleware) return middleware.call(self, payload, next)
    return next(payload)
  }

  __r_createComponents(
    visitors: Required<PluginVisitors>,
    middlewares: Record<string, MiddlewareStartup>
  ) {
    return fromEntries(
      keys(visitors.components).map((key) => {
        const component = middlewares.createViewComponent
          ? middlewares.createViewComponent(visitors.components[key])
          : visitors.components[key]
        return [key, component as ViewComponent]
      })
    )
  }

  __r_createDefinitions(visitors: RequiredPluginVisitors) {
    return this.__r_verifyRegisters(
      visitors.definitions,
      TypedDefinition,
      'TypedDefinition'
    )
  }

  __r_createEnvironments(plugins: RehajePlugin[]): TypedVariable[] {
    return plugins.flatMap((plugin) =>
      this.__r_transformEnvironments(plugin.environments)
    )
  }

  __r_createMiddlewares(plugins: RehajePlugin[]) {
    const middlewaresMaps: Record<string, Middleware[]> = {}
    for (const plugin of plugins) {
      const middlewares = plugin.middlewares ?? {}
      for (const key of keys(middlewares)) {
        middlewaresMaps[key] = middlewaresMaps[key] ?? []
        const middleware = middlewares[key] as Middleware | Middleware[]
        if (isArray(middleware))
          middlewaresMaps[key] = middlewaresMaps[key].concat(
            middleware.filter((fn) => typeof fn === 'function')
          )
        else if (typeof middleware === 'function') {
          middlewaresMaps[key] = middlewaresMaps[key].concat(middleware)
        }
      }
    }
    const middlewares: Record<string, MiddlewareStartup> = {}
    for (const key of keys(middlewaresMaps)) {
      middlewares[key] = createMiddleware(middlewaresMaps[key])
    }
    return middlewares
  }

  __r_createMigrations(plugins: RehajePlugin[]): PluginMigrations {
    const migrations: PluginMigrations = {}
    for (const plugin of plugins) {
      const _migrations = plugin.migrations ?? {}
      for (const key of keys(_migrations)) {
        if (_migrations[key] !== undefined) {
          migrations[key] ??= {}
          assign(migrations[key], _migrations[key])
        }
      }
    }
    return migrations
  }

  __r_createModels(visitors: RequiredPluginVisitors) {
    return this.__r_verifyRegisters(visitors.models, ViewModel, 'ViewModel')
  }

  __r_createNodes(visitors: RequiredPluginVisitors) {
    return this.__r_verifyRegisters(visitors.nodes, TypedNode, 'TypedNode')
  }

  __r_createPlugins(plugins: RehajePlugin[]): RehajePlugin[] {
    const flatPlugins = (plugins: PluginLike[] = []): RehajePlugin[] => {
      const _plugins: RehajePlugin[] = []
      for (const plugin of plugins) {
        if (typeof plugin === 'function') {
          const result = plugin(this.store)
          _plugins.push(...flatPlugins(isArray(result) ? result : [result]))
        } else {
          _plugins.push(
            ...flatPlugins(plugin.plugins),
            this.__r_injectContext(plugin)
          )
        }
      }
      return _plugins
    }
    return flatPlugins(plugins)
  }

  __r_createVisitors(plugins: RehajePlugin[]) {
    const visitors: RequiredPluginVisitors = {
      components: {},
      definitions: {},
      models: {},
      nodes: {}
    }
    for (const plugin of plugins) {
      const _visitors = plugin.visitors ?? {}
      for (const key of keys(_visitors)) {
        if (_visitors[key] !== undefined) assign(visitors[key], _visitors[key])
      }
    }
    return visitors
  }

  __r_injectContext(plugin: RehajePlugin) {
    if (plugin?.visitors !== undefined) {
      for (const [key, visitors] of entries(plugin.visitors)) {
        if (key === 'components') continue
        for (const visitor of values(visitors ?? {})) {
          const proto: StorePluginPrototype = visitor?.prototype ?? visitor
          if (proto) {
            if (!hasOwn(proto, '__r_storePluginMaps')) {
              proto.__r_storePluginMaps = new WeakMap()
            }
            proto.__r_storePluginMaps?.set(this.store, plugin)
            if (!hasOwn(proto, 'getPlugin')) {
              proto.getPlugin = function () {
                return proto.__r_storePluginMaps?.get(this?.__r_store) ?? plugin
              }
            }
          }
        }
      }
    }
    return plugin
  }

  @reactive('action')
  __r_setupPlugins() {
    const _plugins = this.__r_createPlugins(this.plugins)
    const visitors = this.__r_createVisitors(_plugins)
    const middlewares = this.__r_createMiddlewares(_plugins)
    const nodes = this.__r_createNodes(visitors)
    const models = this.__r_createModels(visitors)
    const components = this.__r_createComponents(visitors, middlewares)
    const definitions = this.__r_createDefinitions(visitors)
    const environments = this.__r_createEnvironments(_plugins)
    const migrations = this.__r_createMigrations(_plugins)
    this.visitors = visitors
    this.middlewares = middlewares
    this.nodes = nodes
    this.models = models
    this.components = components
    this.definitions = definitions
    this.environments = environments
    this.migrations = migrations
  }

  __r_transformEnvironments(envs: PluginEnvironment[] = []) {
    return envs.map(({ get, group, name, schema, value }) => {
      return new TypedVariable(() => this.store.node, {
        get,
        group,
        name,
        schema,
        type: 'Block',
        value
      })
    })
  }

  __r_verifyRegisters<T extends TRecord>(
    registers: T,
    baseClass: TClass,
    className: string
  ): T {
    const prototype = baseClass.prototype
    for (const [key, register] of entries(registers)) {
      if (register === undefined) continue
      if (isInheritProto(register.prototype, prototype)) continue
      delete registers[key]
      this.store.raise(
        new FatalAssertInheritError(key, register.name, className)
      )
    }
    return registers
  }

  /**
   * 动态添加插件
   * @param plugin
   */
  addPlugin(plugin: RehajePlugin) {
    this.plugins.push(plugin)
    this.__r_setupPlugins()
    return () => {
      this.removePlugin(plugin)
    }
  }

  /**
   * 调用中间件
   * @param self 上下文
   * @param type 中间件类型
   * @param ctx 数据负载
   * @param next 最后一个任务处理器
   */
  applyMiddleware<T extends keyof RehajeMiddlewares>(
    self: any,
    type: T,
    ctx: Parameters<RehajeMiddlewares[T]>[0],
    next: Next<Parameters<RehajeMiddlewares[T]>[0]>
  ): ReturnType<RehajeMiddlewares[T]> {
    return this.__r_applyMiddleware(self, type, ctx, next) as any
  }

  /**
   * 查询中间件
   * @param type 中间件类型
   */
  getMiddleware(type?: string) {
    if (type) return this.middlewares[type]
  }

  /**
   * 获取升级脚本
   * @param type
   * @param version
   */
  getMigration(type: MigrationTypes, version = '0.0.0') {
    const key = `${type}@${version}`
    if (this.__r_migration_cache.has(key)) return this.__r_migration_cache.get(key)
    const migrations = this.migrations[type]
    if (migrations) {
      const scripts = entries(migrations).toSorted(([version1], [version2]) =>
        compareVersions(version1, version2)
      )
      if (scripts.length > 0) {
        const script = (specific: TypedSpecific) => {
          let result = specific
          for (const [targetVersion, transformer] of scripts) {
            try {
              result = transformer(toJS(result) as any, (message) => {
                throw new LoggerMigrationError(version, targetVersion, message)
              })
              result.version = targetVersion
            } catch (error) {
              this.store.raise(error as Error)
              // FIXME: LoggerMigrationError 是一个 FatalError，会发生中断，这里还能 return 吗？
              return specific
            }
          }
          return result
        }
        this.__r_migration_cache.set(key, script)
        return script
      }
    }
  }

  /**
   * 查询 TypedDefinition 类
   * @param type 定义类型
   */
  getTypedDefinitionClass(type?: string): TypedDefinitionConstructor | void {
    if (type) return this.definitions[type]
  }

  /**
   * 查询 TypedNode 类
   * @param type 节点类型
   */
  getTypedNodeClass(type?: string): TypedNodeConstructor | void {
    if (type) return this.nodes[type]
  }

  /**
   * 查询 ViewComponent 组件
   * @param type 组件类型
   */
  getViewComponentClass(type?: string): ViewComponent | void {
    if (type) return this.components[type]
  }

  /**
   * 查询 ViewModel 类
   * @param type
   */
  getViewModelClass(type?: string): ViewModelConstructor | void {
    if (type) return this.models[type]
  }

  /**
   * 判断是否存在中间件
   * @param type 中间件类型
   */
  hasMiddleware(type?: string) {
    if (type) return !!this.middlewares[type]
    return false
  }

  /**
   * 判断是否存在 TypedDefinition 类
   * @param type 中间件类型
   */
  hasTypedDefinitionClass(type?: string) {
    if (type) return !!this.definitions[type]
    return false
  }

  /**
   * 判断是否存在 TypedNode 类
   * @param type
   */
  hasTypedNodeClass(type?: string) {
    if (type) return !!this.nodes[type]
    return false
  }

  /**
   * 判断是否存在 ViewComponent 组件
   * @param type
   */
  hasViewComponentClass(type?: string) {
    if (type) return !!this.components[type]
    return false
  }

  /**
   * 判断指定类型是否存在 ViewModel 类
   * @param type
   */
  hasViewModelClass(type?: string) {
    if (type) return !!this.models[type]
    return false
  }

  /**
   * 判断指定类型是否存在
   * @param type
   */
  isRegisterType(type: string) {
    return (
      this.hasTypedDefinitionClass(type) ||
      this.hasTypedNodeClass(type) ||
      this.hasViewModelClass(type) ||
      this.hasViewComponentClass(type)
    )
  }

  /**
   * 移除已有插件
   * @param plugin
   */
  removePlugin(plugin: RehajePlugin) {
    this.plugins = this.plugins.filter((_plugin) => _plugin !== plugin)
    this.__r_setupPlugins()
  }
}
